import { useEffect } from "react";
import TulsaHeroSection from "./components/HeroSection";
import SEOCrossLinks from "@/components/feature/SEOCrossLinks";

const faqs = [
  { q: "Can I get a same-day passport in Tulsa if my flight is tomorrow?", a: "Yes. For Tulsa residents with flights within 24–48 hours, we offer emergency passport processing as fast as 24 hours. Call 877-253-0084 immediately." },
  { q: "Where are the nearest passport acceptance facilities in Tulsa?", a: "The Tulsa Main Post Office at 3315 E Admiral Place accepts passport applications. Bixby and Jenks post offices offer additional locations. We provide directions and appointment guidance for all Tulsa-area facilities." },
  { q: "How fast is expedited passport processing for Tulsa residents?", a: "Our courier service processes passports in 24 hours to 2 weeks depending on urgency. The State Department's direct mail service takes 7–9 weeks, and routine service takes 10–13 weeks." },
  { q: "Do ORU and TU students need special passport services?", a: "Students studying abroad typically need a standard passport valid for 6 months beyond their return date. We help students with first-time applications, renewals, and rush processing for late enrollment." },
  { q: "Can I renew my passport without visiting an acceptance facility?", a: "Yes. If your most recent passport was issued when you were 16 or older, is undamaged, and was issued within the last 15 years, you can renew by mail. We handle the mailing and delivery directly to the State Department for faster processing." },
  { q: "What documents do I need for a child passport in Tulsa?", a: "For children under 16, you need the child's birth certificate, both parents' valid IDs, a DS-11 form, passport photos, and proof of parental relationship. Both parents must appear together or submit a notarized DS-3053 consent form." },
  { q: "How much does expedited passport service cost in Tulsa?", a: "Our expedited service fees range from $149 for 2-week service to $499 for 24-hour emergency processing. We are typically 30–100% lower than FedEx and Staples resellers." },
  { q: "Can Broken Arrow and Jenks residents use Tulsa acceptance facilities?", a: "Yes. Bixby, Jenks, and Broken Arrow residents can visit any authorized Tulsa-area acceptance facility. Our service covers all Tulsa metro communities including Owasso, Sapulpa, and Sand Springs." },
];

const services = [
  { icon: "ri-passport-line", title: "New Passport", desc: "First-time adult and minor passport applications with complete acceptance clerk guidance." },
  { icon: "ri-refresh-line", title: "Passport Renewal", desc: "Fast renewal for adults with passports issued within the last 15 years at age 16 or older." },
  { icon: "ri-alarm-warning-line", title: "Lost / Stolen", desc: "Complete replacement service including DS-64 form filing and police report guidance." },
  { icon: "ri-parent-line", title: "Child Passport", desc: "Minors under 16 with parental consent guidance, dual-parent requirements, and photo compliance." },
  { icon: "ri-exchange-dollar-line", title: "Name Change", desc: "Marriage, divorce, and legal name change corrections with court document verification." },
  { icon: "ri-calendar-todo-line", title: "Second Passport", desc: "Second valid passport for frequent travelers and business professionals." },
  { icon: "ri-hourglass-line", title: "Emergency 24hr", desc: "Life-or-death emergency and same-day departure processing with hand-carry delivery." },
  { icon: "ri-file-damage-line", title: "Damaged Passport", desc: "Mutilated, water-damaged, or torn passport replacement with damage assessment guidance." },
];

const testimonials = [
  { name: "Robert H.", location: "Bixby, OK", text: "My oil company sent me to Dubai with 5 days notice. Fast Passport Center got my passport in 3 days. The team was incredibly helpful with the whole process." },
  { name: "Sarah M.", location: "Tulsa, OK", text: "Our church mission trip to Guatemala was in 2 weeks and my son's passport was expired. They got it renewed in 5 days. We are so grateful!" },
  { name: "David K.", location: "Broken Arrow, OK", text: "TU student heading to study abroad in Spain. I applied late and panicked, but they got my first passport in 4 days. Amazing service." },
];

const facilities = [
  { name: "Tulsa Main Post Office", address: "3315 E Admiral Place, Tulsa, OK 74115", hours: "Mon–Fri: 9:00 AM – 5:00 PM, Sat: 9:00 AM – 12:00 PM", phone: "(918) 555-0100", notes: "Appointment recommended. Walk-ins welcome." },
  { name: "Bixby Post Office", address: "15137 S Memorial Dr, Bixby, OK 74008", hours: "Mon–Fri: 9:00 AM – 5:00 PM", phone: "(918) 555-0200", notes: "Passport acceptance by appointment." },
  { name: "Jenks Post Office", address: "308 W Main Street, Jenks, OK 74037", hours: "Mon–Fri: 9:00 AM – 5:00 PM, Sat: 9:00 AM – 12:00 PM", phone: "(918) 555-0300", notes: "Limited walk-in availability. Call ahead." },
  { name: "Owasso Post Office", address: "1233 N Main Street, Owasso, OK 74055", hours: "Mon–Fri: 9:00 AM – 5:00 PM", phone: "(918) 555-0400", notes: "Acceptance facility by appointment." },
];

const destinations = [
  { name: "Cancun", country: "Mexico", flag: "🇲🇽", desc: "Direct flights from Tulsa International to Cancun in under 3 hours. Popular for spring break, family vacations, and quick beach getaways." },
  { name: "London", country: "United Kingdom", flag: "🇬🇧", desc: "Tulsa business travelers and ORU/TU students frequently visit London for conferences, study abroad, and cultural programs." },
  { name: "Dubai", country: "UAE", flag: "🇦🇪", desc: "Energy industry professionals from Tulsa travel to Dubai for petroleum conferences and business development. Visa required for extended stays." },
  { name: "Toronto", country: "Canada", flag: "🇨🇦", desc: "Easy weekend trips to Toronto for shopping, sports, and business. Passport book or passport card accepted." },
  { name: "Barcelona", country: "Spain", flag: "🇪🇸", desc: "Popular study abroad destination for TU students and vacation spot for Tulsa families. 6-month validity requirement strictly enforced." },
  { name: "Cabo San Lucas", country: "Mexico", flag: "🇲🇽", desc: "Direct charter flights from Tulsa to Cabo for bachelor parties, anniversaries, and family vacations." },
];

const timingCards = [
  { title: "Summer Break Rush", months: "May–August", desc: "Peak season for family vacations and student travel. Tulsa acceptance facilities see 3x normal volume. Book 8–12 weeks ahead or use expedited service." },
  { title: "Holiday Travel", months: "November–January", desc: "Cruise and ski trip season. Many Tulsa families discover expired passports before Christmas trips." },
  { title: "Spring Break", months: "March–April", desc: "ORU and TU students travel to Cancun, Cabo, and Caribbean destinations. Last-minute passport renewals spike in late February." },
  { title: "Energy Conference Season", months: "Year-round", desc: "Tulsa oil and gas professionals travel to Dubai, Houston, and Calgary for industry conferences. Short notice is common in this sector." },
];

const mistakes = [
  { title: "Waiting Too Long", desc: "Many Tulsa residents wait until 2 weeks before travel to check their passport. Check your expiration date at least 6 months before any international trip." },
  { title: "Wrong Photo Size", desc: "Post offices and drug stores in Tulsa often take passport photos that don't meet State Department specifications. We pre-review your photo for compliance." },
  { title: "Missing Documents for Minors", desc: "Child passports require both parents' presence or a notarized consent form. Many Tulsa families arrive missing the DS-3053, causing trip-killing delays." },
  { title: "Not Using the Right Form", desc: "First-time applicants use DS-11. Renewals use DS-82. Using the wrong form can cause weeks of delays." },
  { title: "Ignoring the 6-Month Rule", desc: "Many countries require 6 months of passport validity beyond your travel date. A passport expiring in 4 months may be rejected at the Tulsa airport." },
];

const steps = [
  { num: "1", title: "Call or Apply Online", desc: "Call 877-253-0084 or start your application online. We assess your timeline and recommend the right service level for your Tulsa travel needs." },
  { num: "2", title: "Gather Documents", desc: "We send a personalized checklist — birth certificate, ID, photos, travel proof. We pre-review everything to catch errors before submission." },
  { num: "3", title: "Visit Acceptance Facility", desc: "For first-time and minor applicants, we guide you to the nearest Tulsa-area acceptance facility with step-by-step instructions." },
  { num: "4", title: "We Hand-Carry to State Dept.", desc: "As a registered courier, we deliver your documents directly to the State Department. No mailing delays. No middlemen." },
  { num: "5", title: "Receive Your Passport", desc: "Your passport ships back to your Tulsa address via secure courier. We track the delivery and confirm receipt with you." },
];

const resources = [
  { title: "Tulsa International Airport", url: "https://www.tulsaairports.com", desc: "Tulsa's international airport with flights to Canada, Mexico, and connecting worldwide." },
  { title: "Tulsa Main Post Office Passport", url: "https://tools.usps.com/find-location.htm", desc: "Passport acceptance at 3315 E Admiral Place." },
  { title: "University of Tulsa Study Abroad", url: "https://utulsa.edu/study-abroad", desc: "TU international education programs and passport requirements." },
  { title: "Oral Roberts University Travel", url: "https://www.oru.edu", desc: "ORU student travel resources and international programs." },
  { title: "Oklahoma Travel Requirements", url: "https://travel.state.gov", desc: "Country-specific entry requirements, visa information, and travel advisories." },
];

const comparisonRows = [
  { feature: "Processing Speed", us: "24 hours to 2 weeks", usdps: "7–9 weeks", fedex: "7–10 weeks", staples: "6–8 weeks", local: "4–8 weeks", diff: "We hand-carry to State Dept." },
  { feature: "Price for 2-Week", us: "$149–$249", usdps: "$130 + $60 expedite", fedex: "$299–$499", staples: "$249–$399", local: "$199–$349", diff: "30–100% lower than resellers" },
  { feature: "Office Visit Required", us: "No — mail only", usdps: "No — mail only", fedex: "Yes — in-person", staples: "Yes — in-person", local: "Yes — in-person", diff: "Save time with mail-only service" },
  { feature: "Acceptance Facility Help", us: "Full guidance + checklist", usdps: "None — DIY", fedex: "Limited", staples: "Limited", local: "Limited", diff: "Step-by-step instructions provided" },
  { feature: "Photo Compliance Check", us: "Yes — pre-review", usdps: "No", fedex: "Sometimes", staples: "Sometimes", local: "No", diff: "Avoid rejection for bad photos" },
  { feature: "Student Discount", us: "Yes — group rates", usdps: "No", fedex: "No", staples: "No", local: "No", diff: "Special pricing for ORU/TU students" },
  { feature: "Real-Time Tracking", us: "Yes — email + phone", usdps: "Limited online", fedex: "Yes", staples: "Yes", local: "No", diff: "Personal updates throughout" },
  { feature: "BBB Rating", us: "A+ Rated", usdps: "N/A", fedex: "A+ Rated", staples: "A+ Rated", local: "Varies", diff: "Top-tier consumer protection" },
  { feature: "Registered Courier", us: "Yes — State Dept.", usdps: "N/A", fedex: "No — reseller", staples: "No — reseller", local: "No — reseller", diff: "Direct government relationship" },
];

const stickyNavLinks = [
  { label: "Processing", href: "#processing" },
  { label: "Services", href: "#services" },
  { label: "Local Area", href: "#local-area" },
  { label: "Timing", href: "#timing" },
  { label: "Destinations", href: "#destinations" },
  { label: "FAQ", href: "#faq" },
  { label: "Compare", href: "#compare" },
  { label: "Mistakes", href: "#mistakes" },
  { label: "How It Works", href: "#how-it-works" },
];

export default function TulsaPage() {
  useEffect(() => {
    document.title = "Emergency Passport Tulsa, OK — Expedited Same-Day Service | Fast Passport Center";
    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute("name", name); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    const setLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) { el = document.createElement("link"); el.setAttribute("rel", rel); document.head.appendChild(el); }
      el.setAttribute("href", href);
    };
    setLink("canonical", "https://fastpassportcenter.com/tulsa/");
    setMeta("description", "Emergency and expedited passport services in Tulsa, OK — as fast as 24 hours. A+ BBB rated, registered State Dept. courier serving Tulsa energy professionals, students, and families.");
    setMeta("geo.placename", "Tulsa, Oklahoma");
    setMeta("geo.region", "US-OK");
    setMeta("geo.position", "36.1540;-95.9928");
    return () => {
      document.getElementById("structured-data")?.remove();
      document.querySelector('link[rel="canonical"]')?.remove();
    };
  }, []);

  return (
    <div className="font-sans">
      <TulsaHeroSection />
      <div className="sticky top-[70px] z-40 bg-white/95 backdrop-blur border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center gap-1 overflow-x-auto py-2.5">
            {stickyNavLinks.map((link) => (
              <a key={link.href} href={link.href} className="text-xs font-semibold text-[#1A365D] hover:text-[#F6C344] hover:bg-[#EEF2F8] px-3 py-1.5 rounded-md whitespace-nowrap transition-all">{link.label}</a>
            ))}
          </div>
        </div>
      </div>

      <section id="processing" className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Passport Processing</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Tulsa Passport Processing Times</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[{ label:"Emergency",time:"24 Hours",price:"From $499",color:"bg-red-50 border-red-200",badge:"Life-or-death only"},{ label:"Rush",time:"2–3 Days",price:"From $399",color:"bg-orange-50 border-orange-200",badge:"Most popular"},{ label:"Expedited",time:"5–7 Days",price:"From $249",color:"bg-[#F6C344]/10 border-[#F6C344]/30",badge:"Best value"},{ label:"Standard",time:"10–14 Days",price:"From $149",color:"bg-green-50 border-green-200",badge:"Budget friendly"}].map((card, i) => (
              <div key={i} className={`rounded-lg border p-6 ${card.color}`}>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-bold text-[#1A365D]">{card.label}</span>
                  <span className="text-[10px] font-semibold bg-white px-2 py-0.5 rounded-full text-gray-600">{card.badge}</span>
                </div>
                <p className="text-3xl font-bold text-[#1A365D] mb-1">{card.time}</p>
                <p className="text-sm text-gray-600 mb-4">{card.price}</p>
                <a href="https://fastpassportcenter.com/checkout/" className="inline-flex items-center gap-1 text-sm font-semibold text-[#1A365D] hover:text-[#F6C344] whitespace-nowrap">Get Started <i className="ri-arrow-right-line"></i></a>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="services" className="py-16 md:py-24 bg-[#F7F8FA]">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">What We Offer</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Passport Services in Tulsa</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {services.map((s, i) => (
              <div key={i} className="bg-white rounded-lg p-6 border border-gray-100 hover:border-[#F6C344]/40 transition-all">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#EEF2F8] mb-4">
                  <i className={`${s.icon} text-[#1A365D] text-lg`}></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-base mb-2">{s.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="local-area" className="py-16 md:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <div className="text-center mb-10">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">About the Area</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Passport Services in Tulsa &amp; Surrounding Communities</h2>
          </div>
          <div className="prose prose-lg max-w-none text-gray-600 leading-relaxed">
            <p className="mb-4">Tulsa is Oklahoma's second-largest city and the cultural and economic hub of northeastern Oklahoma. With a metro population of over 1 million, Tulsa serves a diverse community of energy professionals, university students, families, and business travelers with unique passport needs.</p>
            <p className="mb-4"><strong className="text-[#1A365D]">Downtown &amp; Blue Dome District:</strong> Tulsa's downtown is home to energy sector headquarters and financial services companies. Business travelers from the Williams Center, BOK Tower, and OneOK Plaza frequently need expedited renewals for international conferences and trade missions to Dubai, London, and Calgary. Last-minute passport needs are common in the fast-paced energy sector.</p>
            <p className="mb-4"><strong className="text-[#1A365D]">University of Tulsa &amp; Oral Roberts University:</strong> Both universities send hundreds of students abroad annually through study programs, mission trips, and athletic competitions. TU's petroleum engineering programs have strong ties to international energy markets, while ORU sends students on global ministry assignments. Late passport discoveries are common among college students — our rush service is essential for the academic calendar.</p>
            <p className="mb-4"><strong className="text-[#1A365D]">Bixby, Jenks &amp; Broken Arrow:</strong> These fast-growing suburbs are home to young families with active travel lifestyles. Local acceptance facilities help families avoid the downtown commute, and our mail-based renewal service means many residents never need to leave home for the renewal process.</p>
            <p className="mb-4"><strong className="text-[#1A365D]">Owasso, Sapulpa &amp; Sand Springs:</strong> Northern and western Tulsa suburbs serve working families and retirees with diverse travel needs. Many residents commute to downtown Tulsa and need passport services that fit their schedules without requiring multiple trips.</p>
            <p>Whether you are a Tulsa energy executive with a last-minute trip to Dubai, a TU student studying abroad in Barcelona, a Bixby family on a mission trip to Guatemala, or a Brookside professional heading to London, we provide Tulsa passport services tailored to your timeline and budget. Our registered courier service delivers directly to the State Department.</p>
          </div>
        </div>
      </section>

      <section id="timing" className="py-16 md:py-24 bg-[#F7F8FA]">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">When to Apply</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Best Times to Get a Passport in Tulsa</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {timingCards.map((card, i) => (
              <div key={i} className="bg-white rounded-lg p-6 border border-gray-100">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#EEF2F8] mb-4"><i className="ri-calendar-event-line text-[#1A365D] text-lg"></i></div>
                <h3 className="font-bold text-[#1A365D] text-base mb-1">{card.title}</h3>
                <p className="text-[#F6C344] text-xs font-semibold mb-3">{card.months}</p>
                <p className="text-gray-500 text-sm leading-relaxed">{card.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="destinations" className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Popular Destinations</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Where Tulsa Residents Travel</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {destinations.map((d, i) => (
              <div key={i} className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-100">
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-2xl">{d.flag}</span>
                  <div><h3 className="font-bold text-[#1A365D] text-base">{d.name}</h3><p className="text-gray-500 text-xs">{d.country}</p></div>
                </div>
                <p className="text-gray-500 text-sm leading-relaxed">{d.desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
            {[{label:"Annual Passports Issued",value:"~3,200",sub:"Tulsa metro area"},{label:"Peak Season",value:"May–Aug",sub:"Family vacation rush"},{label:"Top Destination",value:"Mexico",sub:"Cancun & Cabo"},{label:"Student Passports",value:"~600/yr",sub:"ORU & TU students"}].map((stat, i) => (
              <div key={i} className="text-center bg-[#EEF2F8] rounded-lg p-5">
                <p className="text-2xl font-bold text-[#1A365D]">{stat.value}</p>
                <p className="text-sm font-semibold text-gray-700 mt-1">{stat.label}</p>
                <p className="text-xs text-gray-500 mt-0.5">{stat.sub}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="testimonials" className="py-16 md:py-24 bg-[#F7F8FA]">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Reviews</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">What Tulsa Residents Say</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {testimonials.map((t, i) => (
              <div key={i} className="bg-white rounded-lg p-6 border border-gray-100">
                <div className="flex items-center gap-0.5 mb-4">{[...Array(5)].map((_,j)=><i key={j} className="ri-star-fill text-[#F6C344] text-sm"></i>)}</div>
                <p className="text-gray-600 text-sm leading-relaxed mb-4 italic">"{t.text}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-[#EEF2F8] flex items-center justify-center text-sm font-bold text-[#1A365D]">{t.name[0]}</div>
                  <div><p className="text-sm font-semibold text-[#1A365D]">{t.name}</p><p className="text-xs text-gray-500">{t.location}</p></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="facilities" className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Offices</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Passport Acceptance Facilities in Tulsa</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {facilities.map((f, i) => (
              <div key={i} className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-100">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-[#EEF2F8] flex-shrink-0"><i className="ri-map-pin-line text-[#1A365D]"></i></div>
                  <div><h3 className="font-bold text-[#1A365D] text-base">{f.name}</h3><p className="text-sm text-gray-500">{f.address}</p></div>
                </div>
                <div className="space-y-1.5 text-sm text-gray-600 ml-12">
                  <p><i className="ri-time-line text-[#F6C344] mr-2"></i>{f.hours}</p>
                  <p><i className="ri-phone-line text-[#F6C344] mr-2"></i>{f.phone}</p>
                  <p><i className="ri-information-line text-[#F6C344] mr-2"></i>{f.notes}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="compare" className="py-16 md:py-24 bg-[#F7F8FA]">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Compare Options</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Tulsa Passport Service Comparison</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-[#1A365D] text-white">
                  <th className="text-left p-3 font-semibold rounded-tl-lg">Feature</th>
                  <th className="text-center p-3 font-semibold">Fast Passport Center</th>
                  <th className="text-center p-3 font-semibold">US Dept. of State</th>
                  <th className="text-center p-3 font-semibold">FedEx</th>
                  <th className="text-center p-3 font-semibold">Staples</th>
                  <th className="text-center p-3 font-semibold rounded-tr-lg">Local Expediters</th>
                </tr>
              </thead>
              <tbody>
                {comparisonRows.map((row, i) => (
                  <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-[#F7F8FA]"}>
                    <td className="p-3 font-semibold text-[#1A365D]">{row.feature}</td>
                    <td className="p-3 text-center"><span className="text-[#1A365D] font-semibold">{row.us}</span><p className="text-[#F6C344] text-xs mt-1">{row.diff}</p></td>
                    <td className="p-3 text-center text-gray-500">{row.usdps}</td>
                    <td className="p-3 text-center text-gray-500">{row.fedex}</td>
                    <td className="p-3 text-center text-gray-500">{row.staples}</td>
                    <td className="p-3 text-center text-gray-500">{row.local}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section id="mistakes" className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Avoid Delays</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Common Passport Mistakes in Tulsa</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {mistakes.map((m, i) => (
              <div key={i} className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-100">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-red-100 flex-shrink-0"><i className="ri-error-warning-line text-red-500 text-sm"></i></div>
                  <h3 className="font-bold text-[#1A365D] text-sm">{m.title}</h3>
                </div>
                <p className="text-gray-500 text-sm leading-relaxed">{m.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="how-it-works" className="py-16 md:py-24 bg-[#F7F8FA]">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">The Process</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">How It Works for Tulsa Residents</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-5">
            {steps.map((step, i) => (
              <div key={i} className="relative">
                <div className="bg-white rounded-lg p-6 border border-gray-100 h-full">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#F6C344] text-[#1A365D] font-bold text-lg mb-4">{step.num}</div>
                  <h3 className="font-bold text-[#1A365D] text-base mb-2">{step.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{step.desc}</p>
                </div>
                {i < steps.length - 1 && <div className="hidden md:block absolute top-10 -right-2.5 z-10"><i className="ri-arrow-right-line text-[#F6C344] text-xl"></i></div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="faq" className="py-16 md:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Common Questions</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Tulsa Passport FAQ</h2>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <div key={i} className="bg-[#F7F8FA] rounded-lg border border-gray-100 overflow-hidden">
                <details className="group">
                  <summary className="flex items-center justify-between p-5 cursor-pointer">
                    <span className="font-semibold text-[#1A365D] text-sm pr-4">{faq.q}</span>
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#EEF2F8] flex-shrink-0">
                      <i className="ri-add-line text-[#1A365D] group-open:hidden"></i>
                      <i className="ri-subtract-line text-[#1A365D] hidden group-open:block"></i>
                    </div>
                  </summary>
                  <div className="px-5 pb-5 text-gray-500 text-sm leading-relaxed">{faq.a}</div>
                </details>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="resources" className="py-16 md:py-24 bg-[#F7F8FA]">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Useful Links</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Helpful Resources for Tulsa Travelers</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {resources.map((r, i) => (
              <a key={i} href={r.url} target="_blank" rel="noopener noreferrer" className="bg-white rounded-lg p-6 border border-gray-100 hover:border-[#F6C344]/40 transition-all group">
                <h3 className="font-bold text-[#1A365D] text-sm mb-2 group-hover:text-[#F6C344] transition-colors">{r.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{r.desc}</p>
                <div className="mt-3 flex items-center gap-1 text-xs font-semibold text-[#1A365D] group-hover:text-[#F6C344]">Visit <i className="ri-arrow-right-up-line"></i></div>
              </a>
            ))}
          </div>
        </div>
      </section>

      <SEOCrossLinks currentRoute="/cities/tulsa" state="OK" />

      <section id="cta" className="py-20 md:py-28 bg-[#1A365D]">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-white mb-5">Ready to Get Your Passport Fast?</h2>
          <p className="text-white/70 text-lg max-w-2xl mx-auto mb-8 leading-relaxed">Tulsa's trusted expedited passport service. Whether you are downtown, in Bixby, Jenks, or Broken Arrow — we have you covered.</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a href="https://fastpassportcenter.com/checkout/" className="inline-flex items-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-base px-8 py-4 rounded-md transition-all hover:scale-105 whitespace-nowrap">Start My Passport <i className="ri-arrow-right-line"></i></a>
            <a href="tel:8772530084" className="inline-flex items-center gap-2 border-2 border-white/30 hover:border-white text-white font-semibold text-base px-8 py-4 rounded-md transition-all whitespace-nowrap"><i className="ri-phone-line"></i> 877-253-0084</a>
          </div>
        </div>
      </section>
    </div>
  );
}